import React, { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Outlet,
} from "react-router";
import { Helmet } from "react-helmet-async";

// Route wrappers
import { PublicRouteWrapper } from "@/routes/wrappers/PublicRouteWrapper";
import { ProtectedRouteWrapper } from "@/routes/wrappers/ProtectedRouteWrapper";
import { AdminRouteWrapper } from "@/routes/wrappers/AdminRouteWrapper";
import { OnboardingRouteWrapper } from "@/routes/wrappers/OnboardingRouteWrapper";
import { LegalRouteWrapper } from "@/routes/wrappers/LegalRouteWrapper";
import { FamilyRouteWrapper } from "@/routes/wrappers/FamilyRouteWrapper";
import { LawyerRouteWrapper } from "@/routes/wrappers/LawyerRouteWrapper";
import { LawyerAuthRouteWrapper } from "@/routes/wrappers/LawyerAuthRouteWrapper";

// Route definitions
import { authRoutes } from "@/routes/auth/AuthRoutes";
import { publicRoutes } from "@/routes/public/PublicRoutes";
import { DemoProvider } from "@/pages/demo/DemoProvider";
import { DEMO_ROUTES } from "@/shared/constants/paths";
import { LawyerDashboard } from "@/pages/lawyer/dashboard/DashboardPage";
import LawyerClientDetail from "@/pages/lawyer/client-detail/ClientDetailPage";
import { onboardingRoutes } from "@/routes/onboarding/OnboardingRoutes";
import { postLossRoutes } from "@/routes/postloss/PostLossRoutes";
import { adminRoutes } from "@/routes/admin/AdminRoutes";
import { legalRoutes } from "@/routes/legal/LegalRoutes";
import { familyRoutes } from "@/routes/family/FamilyRoutes";
import { lawyerAuthRoutes, lawyerProtectedRoutes } from "@/routes/lawyer/LawyerRoutes";

// Pages
import NotFound from "@/pages/NotFound";
import RootRedirect from "@/shared/components/routing/RootRedirect";
import ResetPasswordPage from "@/pages/auth/reset-password/ResetPasswordPage";
import { AUTH_ROUTES } from "@/shared/constants/paths";

// Error Boundary
import { RouterErrorBoundary } from "@/shared/components/routing/RouterErrorBoundary";

// Hooks
import { getEnvironment } from "@/shared/hooks/useGetEnvironment";

// Providers
import { ActivityTrackingProvider } from "@/shared/providers/ActivityTrackingProvider";
import { ImpersonationProvider } from "@/shared/providers/ImpersonationProvider";

// Components
import { ImpersonationBanner } from "@/shared/components/ImpersonationBanner";

// Dev panel (lazy-loaded, available on local and dev environments)
const isDevEnvironment = import.meta.env.DEV || getEnvironment() === "dev";
const LazyDevPanel = isDevEnvironment
  ? lazy(() => import("@/shared/components/DevPanel").then((m) => ({ default: m.DevPanel })))
  : null;

const RootLayout = () => {
  return (
    <ImpersonationProvider>
      <ActivityTrackingProvider>
        <ImpersonationBanner />
        <Outlet />
        {LazyDevPanel && (
          <Suspense fallback={null}>
            <LazyDevPanel />
          </Suspense>
        )}
      </ActivityTrackingProvider>
    </ImpersonationProvider>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<RootLayout />} errorElement={<RouterErrorBoundary />}>
      {/* Root redirect - checks authentication status */}
      <Route index element={<RootRedirect />} />

      {/* Reset Password - Standalone route without wrapper */}
      <Route
        path={AUTH_ROUTES.RESET_PASSWORD}
        element={
          <>
            <Helmet>
              <title>Reset Password - Elayne</title>
            </Helmet>
            <ResetPasswordPage />
          </>
        }
      />

      {/* Demo Routes - Public, no auth required */}
      <Route
        element={
          <DemoProvider>
            <Outlet />
          </DemoProvider>
        }
      >
        <Route path={DEMO_ROUTES.LAWYER} element={<LawyerDashboard />} />
        <Route path={DEMO_ROUTES.LAWYER_CLIENT} element={<LawyerClientDetail />} />
      </Route>

      {/* Public Routes - Accessible without authentication */}
      <Route element={<PublicRouteWrapper />}>{publicRoutes}</Route>

      {/* Auth Routes - Authentication related routes */}
      <Route element={<PublicRouteWrapper />}>{authRoutes}</Route>

      {/* Onboarding Routes - User onboarding flows */}
      <Route element={<OnboardingRouteWrapper />}>{onboardingRoutes}</Route>

      {/* Post Loss Routes - Main application routes (protected) */}
      <Route element={<ProtectedRouteWrapper />}>{postLossRoutes}</Route>

      {/* Admin Routes - Administrative routes (admin only) */}
      <Route element={<AdminRouteWrapper />}>{adminRoutes}</Route>

      {/* Legal Routes - Always accessible */}
      <Route element={<LegalRouteWrapper />}>{legalRoutes}</Route>

      {/* Family Routes - Family sharing and invitations */}
      <Route element={<FamilyRouteWrapper />}>{familyRoutes}</Route>

      {/* Lawyer Auth Routes - Redirects authenticated users away */}
      <Route element={<LawyerAuthRouteWrapper />}>{lawyerAuthRoutes}</Route>

      {/* Lawyer Protected Routes - Lawyer only */}
      <Route element={<LawyerRouteWrapper />}>{lawyerProtectedRoutes}</Route>

      {/* 404 Page */}
      <Route
        path="*"
        element={
          <>
            <Helmet>
              <title>Page Not Found - Elayne</title>
            </Helmet>
            <NotFound />
          </>
        }
      />
    </Route>,
  ),
);

const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
